import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {useAuth} from "../context/AuthContext";

const Registration = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        dateOfBirth: '',
        gender: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        primaryIdFile: null,
        secondaryIdFile: null,
        agreeToTerms: false,
        receiveUpdates: true
    });

    const [beneficiaries, setBeneficiaries] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [uploadProgress, setUploadProgress] = useState({});
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleFileUpload = async (file, type) => {
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

        // Validate file
        if (!file) {
            setErrors(prev => ({ ...prev, [type]: 'File is required' }));
            return;
        }

        if (file.size > maxSize) {
            setErrors(prev => ({ ...prev, [type]: 'File size must be less than 5MB' }));
            return;
        }

        if (!allowedTypes.includes(file.type)) {
            setErrors(prev => ({ ...prev, [type]: 'File must be JPG, PNG, or PDF' }));
            return;
        }

        // Simulate upload progress
        setUploadProgress(prev => ({ ...prev, [type]: 0 }));

        const interval = setInterval(() => {
            setUploadProgress(prev => {
                const newProgress = (prev[type] || 0) + 20;
                if (newProgress >= 100) {
                    clearInterval(interval);
                    setFormData(prev => ({ ...prev, [type]: file }));
                    setErrors(prev => ({ ...prev, [type]: null }));
                    return { ...prev, [type]: 100 };
                }
                return { ...prev, [type]: newProgress };
            });
        }, 200);
    };

    const addBeneficiary = () => {
        const newBeneficiary = {
            id: Date.now(),
            name: '',
            relationship: '',
            dateOfBirth: '',
            idNumber: '',
            idFile: null,
            percentage: 0,
            errors: {}
        };
        setBeneficiaries(prev => [...prev, newBeneficiary]);
    };

    const updateBeneficiary = (index, field, value) => {
        setBeneficiaries(prev => prev.map((beneficiary, i) =>
            i === index ? { ...beneficiary, [field]: value } : beneficiary
        ));
    };

    const removeBeneficiary = (index) => {
        setBeneficiaries(prev => prev.filter((_, i) => i !== index));
    };

    const validateStep = (step) => {
        const newErrors = {};

        if (step === 1) {
            if (!formData.firstName.trim()) {
                newErrors.firstName = 'First name is required';
            }
            if (!formData.lastName.trim()) {
                newErrors.lastName = 'Last name is required';
            }
            if (!formData.email) {
                newErrors.email = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = 'Email is invalid';
            }
            if (!formData.phone) {
                newErrors.phone = 'Phone number is required';
            }
            if (!formData.dateOfBirth) {
                newErrors.dateOfBirth = 'Date of birth is required';
            } else {
                const birthDate = new Date(formData.dateOfBirth);
                const today = new Date();
                const age = today.getFullYear() - birthDate.getFullYear();
                if (age < 18) {
                    newErrors.dateOfBirth = 'You must be at least 18 years old';
                }
            }
        }

        if (step === 2) {
            if (!formData.primaryIdFile) {
                newErrors.primaryIdFile = 'Primary ID is required';
            }
            if (!formData.secondaryIdFile) {
                newErrors.secondaryIdFile = 'Secondary ID is required';
            }

            // Validate beneficiaries
            beneficiaries.forEach((beneficiary, index) => {
                const beneficiaryErrors = {};
                if (!beneficiary.name.trim()) {
                    beneficiaryErrors.name = 'Beneficiary name is required';
                }
                if (!beneficiary.relationship) {
                    beneficiaryErrors.relationship = 'Relationship is required';
                }
                if (!beneficiary.dateOfBirth) {
                    beneficiaryErrors.dateOfBirth = 'Date of birth is required';
                }
                if (!beneficiary.idFile) {
                    beneficiaryErrors.idFile = 'ID document is required';
                }
                if (Object.keys(beneficiaryErrors).length > 0) {
                    newErrors[`beneficiary_${index}`] = beneficiaryErrors;
                }
            });
        }

        if (step === 3) {
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
                newErrors.password = 'Password must contain uppercase, lowercase, and numbers';
            }

            if (!formData.confirmPassword) {
                newErrors.confirmPassword = 'Please confirm your password';
            } else if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }

            if (!formData.emergencyContactName.trim()) {
                newErrors.emergencyContactName = 'Emergency contact name is required';
            }
            if (!formData.emergencyContactPhone) {
                newErrors.emergencyContactPhone = 'Emergency contact phone is required';
            }
            if (!formData.agreeToTerms) {
                newErrors.agreeToTerms = 'You must agree to the terms and conditions';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => prev - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateStep(currentStep)) {
            return;
        }

        if (currentStep < 3) {
            nextStep();
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        try {
            const result = await register({
                ...formData,
                beneficiaries: beneficiaries.map(b => ({
                    name: b.name,
                    relationship: b.relationship,
                    dateOfBirth: b.dateOfBirth,
                    idNumber: b.idNumber,
                    percentage: b.percentage
                }))
            });

            if (result.success) {
                navigate('/dashboard');
            } else {
                setErrors({ submit: result.error });
            }
        } catch (error) {
            setErrors({ submit: 'An unexpected error occurred. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const FileUploadField = ({ label, name, accept, required = false, file = null }) => (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                <input
                    type="file"
                    name={name}
                    accept={accept}
                    onChange={(e) => handleFileUpload(e.target.files[0], name)}
                    className="hidden"
                    id={name}
                />
                <label htmlFor={name} className="cursor-pointer block">
                    {file ? (
                        <div className="space-y-2">
                            <div className="text-green-600 text-lg">✅</div>
                            <p className="text-sm font-medium text-gray-900">
                                {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                                Click to change file
                            </p>
                            {uploadProgress[name] !== undefined && uploadProgress[name] < 100 && (
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress[name]}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="text-gray-400 text-lg">📄</div>
                            <p className="text-sm font-medium text-gray-900">
                                Click to upload {label.toLowerCase()}
                            </p>
                            <p className="text-xs text-gray-500">
                                Supports JPG, PNG, PDF (Max 5MB)
                            </p>
                        </div>
                    )}
                </label>
            </div>
            {errors[name] && (
                <p className="text-red-500 text-sm mt-1">{errors[name]}</p>
            )}
        </div>
    );

    const BeneficiaryForm = ({ beneficiary, index, onChange, onRemove }) => {
        const handleBeneficiaryFileUpload = (file) => {
            const maxSize = 5 * 1024 * 1024;
            const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];

            if (!file) return;

            if (file.size > maxSize) {
                onChange(index, 'errors', { ...beneficiary.errors, idFile: 'File size must be less than 5MB' });
                return;
            }

            if (!allowedTypes.includes(file.type)) {
                onChange(index, 'errors', { ...beneficiary.errors, idFile: 'File must be JPG, PNG, or PDF' });
                return;
            }

            onChange(index, 'idFile', file);
            onChange(index, 'errors', { ...beneficiary.errors, idFile: null });
        };

        return (
            <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold text-gray-900">Beneficiary {index + 1}</h4>
                    <button
                        type="button"
                        onClick={() => onRemove(index)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                    >
                        Remove
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name *
                        </label>
                        <input
                            type="text"
                            value={beneficiary.name}
                            onChange={(e) => onChange(index, 'name', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                beneficiary.errors?.name ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter beneficiary's full name"
                        />
                        {beneficiary.errors?.name && (
                            <p className="text-red-500 text-sm mt-1">{beneficiary.errors.name}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Relationship *
                        </label>
                        <select
                            value={beneficiary.relationship}
                            onChange={(e) => onChange(index, 'relationship', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                beneficiary.errors?.relationship ? 'border-red-500' : 'border-gray-300'
                            }`}
                        >
                            <option value="">Select relationship</option>
                            <option value="spouse">Spouse</option>
                            <option value="child">Child</option>
                            <option value="parent">Parent</option>
                            <option value="sibling">Sibling</option>
                            <option value="other">Other</option>
                        </select>
                        {beneficiary.errors?.relationship && (
                            <p className="text-red-500 text-sm mt-1">{beneficiary.errors.relationship}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date of Birth *
                        </label>
                        <input
                            type="date"
                            value={beneficiary.dateOfBirth}
                            onChange={(e) => onChange(index, 'dateOfBirth', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                beneficiary.errors?.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {beneficiary.errors?.dateOfBirth && (
                            <p className="text-red-500 text-sm mt-1">{beneficiary.errors.dateOfBirth}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            ID Number
                        </label>
                        <input
                            type="text"
                            value={beneficiary.idNumber}
                            onChange={(e) => onChange(index, 'idNumber', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Government ID number"
                        />
                    </div>
                </div>

                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        ID Document *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                        <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => handleBeneficiaryFileUpload(e.target.files[0])}
                            className="hidden"
                            id={`beneficiary-id-${index}`}
                        />
                        <label htmlFor={`beneficiary-id-${index}`} className="cursor-pointer block">
                            {beneficiary.idFile ? (
                                <div className="space-y-2">
                                    <div className="text-green-600 text-lg">✅</div>
                                    <p className="text-sm font-medium text-gray-900">
                                        {beneficiary.idFile.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Click to change file
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="text-gray-400 text-lg">📄</div>
                                    <p className="text-sm font-medium text-gray-900">
                                        Upload Beneficiary ID Document
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Government-issued ID (Max 5MB)
                                    </p>
                                </div>
                            )}
                        </label>
                    </div>
                    {beneficiary.errors?.idFile && (
                        <p className="text-red-500 text-sm mt-1">{beneficiary.errors.idFile}</p>
                    )}
                </div>

                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Benefit Percentage *
                    </label>
                    <div className="flex items-center space-x-4">
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={beneficiary.percentage}
                            onChange={(e) => onChange(index, 'percentage', parseInt(e.target.value))}
                            className="w-full"
                        />
                        <span className="text-sm font-medium text-gray-700 w-12">
              {beneficiary.percentage}%
            </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Percentage of benefits this beneficiary will receive
                    </p>
                </div>
            </div>
        );
    };

    const ProgressBar = () => (
        <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Step {currentStep} of 3</span>
                <span className="text-sm text-gray-500">
          {currentStep === 1 && 'Personal Information'}
                    {currentStep === 2 && 'ID Verification & Beneficiaries'}
                    {currentStep === 3 && 'Account Setup'}
        </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                    className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep / 3) * 100}%` }}
                ></div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl w-full space-y-8">
                {/* Header */}
                <div className="text-center">
                    <Link to="/" className="inline-block">
                        <div className="mx-auto h-16 w-16 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4">
                            C
                        </div>
                    </Link>
                    <h2 className="text-3xl font-extrabold text-gray-900">
                        Join Chema Community
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Complete your registration with ID verification and beneficiary setup
                    </p>
                </div>

                {/* Form Container */}
                <div className="bg-white py-8 px-6 shadow rounded-lg sm:px-10">
                    <ProgressBar />

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {/* Step 1: Personal Information */}
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                            First Name *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="firstName"
                                                name="firstName"
                                                type="text"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Enter your first name"
                                            />
                                            {errors.firstName && (
                                                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                            Last Name *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="lastName"
                                                name="lastName"
                                                type="text"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Enter your last name"
                                            />
                                            {errors.lastName && (
                                                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                            Email Address *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.email ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Enter your email address"
                                            />
                                            {errors.email && (
                                                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                            Phone Number *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.phone ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Enter your phone number"
                                            />
                                            {errors.phone && (
                                                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                                            Date of Birth *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="dateOfBirth"
                                                name="dateOfBirth"
                                                type="date"
                                                value={formData.dateOfBirth}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.dateOfBirth ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                            />
                                            {errors.dateOfBirth && (
                                                <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                                            Gender
                                        </label>
                                        <div className="mt-1">
                                            <select
                                                id="gender"
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleInputChange}
                                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                            >
                                                <option value="">Select gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                                <option value="prefer-not-to-say">Prefer not to say</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2: ID Verification & Beneficiaries */}
                        {currentStep === 2 && (
                            <div className="space-y-8">
                                {/* ID Verification Section */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">ID Verification</h3>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                        <h4 className="font-medium text-blue-900 mb-2">📋 Required Documents</h4>
                                        <ul className="text-sm text-blue-800 space-y-1">
                                            <li>• Primary ID: National ID, Driver's License, or Passport</li>
                                            <li>• Secondary ID: Any other government-issued ID</li>
                                            <li>• Files must be clear and readable</li>
                                            <li>• Maximum file size: 5MB per document</li>
                                        </ul>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <FileUploadField
                                            label="Primary ID Document"
                                            name="primaryIdFile"
                                            accept=".jpg,.jpeg,.png,.pdf"
                                            required
                                            file={formData.primaryIdFile}
                                        />

                                        <FileUploadField
                                            label="Secondary ID Document"
                                            name="secondaryIdFile"
                                            accept=".jpg,.jpeg,.png,.pdf"
                                            required
                                            file={formData.secondaryIdFile}
                                        />
                                    </div>
                                </div>

                                {/* Beneficiaries Section */}
                                <div>
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold text-gray-900">Beneficiaries</h3>
                                        <button
                                            type="button"
                                            onClick={addBeneficiary}
                                            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                                        >
                                            + Add Beneficiary
                                        </button>
                                    </div>

                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                                        <h4 className="font-medium text-yellow-900 mb-2">💡 Important Information</h4>
                                        <ul className="text-sm text-yellow-800 space-y-1">
                                            <li>• Beneficiaries will receive support in case of your passing</li>
                                            <li>• You can add multiple beneficiaries and assign percentages</li>
                                            <li>• Total percentage must equal 100%</li>
                                            <li>• Each beneficiary requires valid ID verification</li>
                                        </ul>
                                    </div>

                                    <div className="space-y-4">
                                        {beneficiaries.map((beneficiary, index) => (
                                            <BeneficiaryForm
                                                key={beneficiary.id}
                                                beneficiary={beneficiary}
                                                index={index}
                                                onChange={updateBeneficiary}
                                                onRemove={removeBeneficiary}
                                            />
                                        ))}

                                        {beneficiaries.length === 0 && (
                                            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                                <div className="text-gray-400 text-4xl mb-4">👥</div>
                                                <h4 className="text-lg font-medium text-gray-900 mb-2">No Beneficiaries Added</h4>
                                                <p className="text-gray-600 mb-4">
                                                    Add at least one beneficiary who will receive support in case of need.
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={addBeneficiary}
                                                    className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors font-medium"
                                                >
                                                    Add Your First Beneficiary
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Account Setup */}
                        {currentStep === 3 && (
                            <div className="space-y-6">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <h3 className="text-sm font-medium text-green-800 mb-2">
                                        ✅ Verification Complete
                                    </h3>
                                    <p className="text-sm text-green-700">
                                        Your documents and beneficiary information have been received.
                                        Your account will be activated after verification (usually within 24 hours).
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                            Password *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="password"
                                                name="password"
                                                type="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.password ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Create a strong password"
                                            />
                                            {errors.password && (
                                                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                            Confirm Password *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                type="password"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Confirm your password"
                                            />
                                            {errors.confirmPassword && (
                                                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="emergencyContactName" className="block text-sm font-medium text-gray-700">
                                            Emergency Contact Name *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="emergencyContactName"
                                                name="emergencyContactName"
                                                type="text"
                                                value={formData.emergencyContactName}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.emergencyContactName ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Full name of emergency contact"
                                            />
                                            {errors.emergencyContactName && (
                                                <p className="mt-1 text-sm text-red-600">{errors.emergencyContactName}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-gray-700">
                                            Emergency Contact Phone *
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="emergencyContactPhone"
                                                name="emergencyContactPhone"
                                                type="tel"
                                                value={formData.emergencyContactPhone}
                                                onChange={handleInputChange}
                                                className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                                    errors.emergencyContactPhone ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Phone number of emergency contact"
                                            />
                                            {errors.emergencyContactPhone && (
                                                <p className="mt-1 text-sm text-red-600">{errors.emergencyContactPhone}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-start">
                                        <input
                                            id="agreeToTerms"
                                            name="agreeToTerms"
                                            type="checkbox"
                                            checked={formData.agreeToTerms}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                                        />
                                        <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-900">
                                            I agree to the{' '}
                                            <a href="#" className="text-primary-600 hover:text-primary-500 font-medium">
                                                Terms and Conditions
                                            </a>{' '}
                                            and{' '}
                                            <a href="#" className="text-primary-600 hover:text-primary-500 font-medium">
                                                Privacy Policy
                                            </a>{' '}
                                            of Chema Community *
                                        </label>
                                    </div>
                                    {errors.agreeToTerms && (
                                        <p className="text-sm text-red-600">{errors.agreeToTerms}</p>
                                    )}

                                    <div className="flex items-start">
                                        <input
                                            id="receiveUpdates"
                                            name="receiveUpdates"
                                            type="checkbox"
                                            checked={formData.receiveUpdates}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                                        />
                                        <label htmlFor="receiveUpdates" className="ml-2 block text-sm text-gray-900">
                                            I want to receive updates about community activities and support cases
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Submit Error */}
                        {errors.submit && (
                            <div className="rounded-md bg-red-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-red-800">
                                            {errors.submit}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex space-x-4">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                >
                                    Previous
                                </button>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                    currentStep === 1 ? 'flex-1' : 'flex-1'
                                }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {currentStep === 3 ? 'Creating Account...' : 'Processing...'}
                                    </>
                                ) : (
                                    currentStep === 3 ? 'Complete Registration' : 'Continue'
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Features */}
                <div className="text-center">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Your Security is Our Priority
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
                        <div className="flex flex-col items-center">
                            <div className="text-2xl mb-2">🔒</div>
                            <p className="font-medium text-gray-900">Secure Verification</p>
                            <p className="mt-1">All documents are encrypted and securely stored</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="text-2xl mb-2">👥</div>
                            <p className="font-medium text-gray-900">Beneficiary Protection</p>
                            <p className="mt-1">Your loved ones are protected with proper documentation</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="text-2xl mb-2">⚡</div>
                            <p className="font-medium text-gray-900">Quick Processing</p>
                            <p className="mt-1">Account verification typically within 24 hours</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Registration;